import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

// Where a workspace runs its agents (specifications/cloud-runners.md). Shapes
// mirror api.md §Runner settings.

export type RunnerMode = "cloud" | "self_hosted";

// One cloud runner size (cloud-runners.md §Sizes).
export type ApiCloudRunnerSize = {
  readonly key: string;
  readonly label: string;
  readonly max_sandboxes: number;
  readonly memory_gb: number;
  // What Tinsmith charges; sent, and shown, with billing off too.
  readonly usd_per_hour: number;
  readonly vcpus: number;
};

export type ApiCloudRunnerSettings = {
  // How long an empty runner waits for the next task before it stops.
  readonly idle_minutes: number;
  readonly idle_minutes_limit: number;
  readonly max_runners: number;
  readonly max_runners_limit: number;
  readonly sandboxes_per_runner: number;
  readonly size: string;
  readonly sizes: ReadonlyArray<ApiCloudRunnerSize>;
};

export type ApiRunnerSettings = {
  // Null where the deployment offers no cloud runners; `mode` is then always
  // `self_hosted`.
  readonly cloud: ApiCloudRunnerSettings | null;
  readonly mode: RunnerMode;
};

export type UseRunnerSettings = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly settings: ApiRunnerSettings | null;
};

export const useRunnerSettings = (
  workspaceId: string | null,
): UseRunnerSettings => {
  const { data, error, loading, refetch } = useResource<ApiRunnerSettings>(
    workspaceId ? `/workspaces/${workspaceId}/runner-settings` : null,
  );
  return { error, loading, refetch, settings: data ?? null };
};

// The cloud fields are sent in either mode: self-hosted keeps them, so
// switching back to cloud restores them.
export type RunnerSettingsUpdate = {
  readonly idle_minutes: number;
  readonly max_runners: number;
  readonly mode: RunnerMode;
  readonly sandboxes_per_runner: number;
  readonly size: string;
};

// Sensitive: a stale session is challenged with `re_auth_required`, which the
// client re-verifies and replays like any other call.
export const updateRunnerSettings = (
  workspaceId: string,
  settings: RunnerSettingsUpdate,
): Promise<ApiRunnerSettings> =>
  api.put(`/workspaces/${workspaceId}/runner-settings`, settings);
